import { ComponentProps } from 'react';
import {
  CloseButtonProps,
  ToastContainer as BaseToastContainer,
  TypeOptions,
} from 'react-toastify';

import { Button, Icon, keyframes, styled } from '@project44/manifest-ds';

// Animations

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const fadeOut = keyframes({
  form: { opacity: 1 },
  to: { opacity: 0 },
});

const growIn = keyframes({
  from: { maxHeight: '30px' },
  to: {
    maxHeight: '112px',
  },
});

const slideInRight = keyframes({
  from: { transform: 'translate(100%, 0)', visibility: 'visible' },
  to: { transform: 'translate(0, 0)' },
});

const slideOutRight = keyframes({
  from: { transform: 'transform: translate(0, 0)' },
  to: {
    visibility: 'hidden',
    transform: 'translate(100%, 0)',
  },
});

// Styled Components

const StyledToastContainer = styled(BaseToastContainer, {
  '.Toastify__toast-body': {
    alignItems: 'flex-start',
    padding: 0,
  },
  '&.Toastify__toast-container': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: '440px',
    top: '7em',
  },
  '.Toastify__toast-icon': {
    marginRight: '$large',
  },
  '.grow-in': {
    animation: `${fadeIn} 500ms ease both, ${growIn} 300ms ease-out both`,
  },
  '.fade-out': {
    animation: `${fadeOut} 500ms ease both`,
  },
  '.slide-in-right': {
    animation: `${slideInRight} 500ms ease both`,
  },
  '.slide-out-right': {
    animation: `${slideOutRight} 250ms ease both`,
  },
  '.toast-base': {
    display: 'flex',
    alignItems: 'flex-start',
    backgroundColor: '$palette-white',
    padding: '$medium',
    borderRadius: '$small',
    boxShadow: '$medium',
    gap: '$medium',
    marginBottom: '$medium',
    width: ' 100%',
    overflow: 'hidden',
  },
  '.toast--pending': {
    '.Toastify__toast-icon': {
      alignSelf: 'center',
    },
  },
  '.toast--success': {
    borderLeft: '4px solid $palette-green-600',
  },
  '.toast--error': {
    borderLeft: '4px solid $palette-red-600',
  },
});

const contextClass: Record<TypeOptions, string> = {
  success: 'toast--success',
  error: 'toast--error',
  default: 'toast--pending',
  warning: '',
  info: '',
};

function CloseButton({ closeToast }: CloseButtonProps) {
  return (
    <Button
      css={{ padding: '$small' }}
      size="small"
      variant="tertiary"
      onClick={closeToast}
    >
      <Icon icon="close" />
    </Button>
  );
}

function ToastContainer(props: ComponentProps<typeof StyledToastContainer>) {
  return (
    <StyledToastContainer
      toastClassName={(context) =>
        'toast-base ' + contextClass[context?.type || 'default']
      }
      closeButton={CloseButton}
      {...props}
    />
  );
}

export default ToastContainer;
